import {extendObservable, computed, action, decorate} from 'mobx';
import * as R from 'ramda/src/index';

const INIT_MIN_VALUE = 0;
const INIT_MAX_VALUE = 100;

const getSortEventLabel = (selectedValue) => {
  switch (selectedValue) {
  case 'obDuration':
    return 'duration-dep';
  case 'ibDuration':
    return 'duration-ret';
  default:
    return 'price';
  }
};

class FilterStore {
  constructor({routes, resultsEventsChannel}) {
    extendObservable(this, {
      sorting: [
        {
          value: 'price',
          selected: true
        },
        ...routes.reduce((acc, route, i) => {
          acc.push(
            {
              name: route,
              value: `legDuration-${i}`,
              selected: false
            },
            {
              name: route,
              value: `legDepDate-${i}`,
              selected: false
            },
            {
              name: route,
              value: `legArrDate-${i}`,
              selected: false
            }
          );
          return acc;
        }, [])
      ],
      price: {
        value: null,
        min: INIT_MIN_VALUE,
        max: INIT_MAX_VALUE
      },
      stops: {
        defaultValue: 0,
        value: null,
        options: []
      },
      airlines: {
        options: [],
        selectedOptions: [],
        withCombinations: null
      },
      providers: {
        options: [],
        selectedOptions: []
      },
      transportTypes: {
        options: [],
        selectedOptions: []
      },
      obTimes: {
        departure: {
          min: INIT_MIN_VALUE,
          max: INIT_MAX_VALUE,
          value: [INIT_MIN_VALUE, INIT_MAX_VALUE]
        },
        arrival: {
          min: INIT_MIN_VALUE,
          max: INIT_MAX_VALUE,
          value: [INIT_MIN_VALUE, INIT_MAX_VALUE]
        },
        duration: {
          min: INIT_MIN_VALUE,
          max: INIT_MAX_VALUE,
          value: INIT_MAX_VALUE
        }
      },
      ibTimes: {
        departure: {
          min: INIT_MIN_VALUE,
          max: INIT_MAX_VALUE,
          value: [INIT_MIN_VALUE, INIT_MAX_VALUE]
        },
        arrival: {
          min: INIT_MIN_VALUE,
          max: INIT_MAX_VALUE,
          value: [INIT_MIN_VALUE, INIT_MAX_VALUE]
        },
        duration: {
          min: INIT_MIN_VALUE,
          max: INIT_MAX_VALUE,
          value: INIT_MAX_VALUE
        }
      }
    });

    Object.assign(this, {resultsEventsChannel});
  }

  setupFilters = (filtersOptions) => {
    filtersOptions.airlines.selectedOptions = R.pipe(
      R.difference(filtersOptions.airlines.options),
      R.pluck('code'),
      R.concat(this.airlines.selectedOptions),
      R.uniq
    )(this.airlines.options);
    filtersOptions.providers.selectedOptions = R.pipe(
      R.difference(filtersOptions.providers.options),
      R.pluck('name'),
      R.concat(this.providers.selectedOptions),
      R.uniq
    )(this.providers.options);
    // filtersOptions.transportTypes.selectedOptions = R.pipe(
    //   R.difference(filtersOptions.transportTypes.options),
    //   R.pluck('name'),
    //   R.concat(this.transportTypes.selectedOptions),
    //   R.uniq
    // )(this.transportTypes.options);

    Object.assign(this, R.mergeDeepRight(this, filtersOptions));
  };

  toggleProvider = (val) => {
    const selectedIndex = this.providers.selectedOptions.indexOf(val);
    if (selectedIndex !== -1) {
      this.providers.selectedOptions.splice(selectedIndex, 1);
    } else {
      this.providers.selectedOptions.push(val);
    }
    this.resultsEventsChannel.publish('filters.provider.change');
  };

  selectSingleProvider = (val) => {
    this.providers.selectedOptions = [val];
    this.resultsEventsChannel.publish('filters.provider.change');
  };

  toggleTransportation = (val) => {
    const selectedIndex = this.transportTypes.selectedOptions.indexOf(val);
    if (selectedIndex !== -1) {
      this.transportTypes.selectedOptions.splice(selectedIndex, 1);
    } else {
      this.transportTypes.selectedOptions.push(val);
    }
  };

  selectSingleTransportation = (val) => {
    this.transportTypes.selectedOptions = [val];
  };

  toggleAllTransportations = (forced) => {
    this.transportTypes.selectedOptions = forced || !this.allTransportationsAreChecked
      ? this.transportTypes.options.map(({name}) => name)
      : [];
  };

  updatePriceValue = (val) => {
    this.price.value = val;
  };

  resetPriceValue = () => {
    this.price.value = this.price.max;
  };

  updateFlightTimeValue = (way, type) => {
    return action((val) => {
      this[way][type].value = val;
    });
  };

  resetFlightTimeValue = (way, type) => {
    return action(() => {
      if (type === 'duration') {
        this[way][type].value = this[way][type].max;
      } else {
        this[way][type].value = [
          this[way][type].min,
          this[way][type].max
        ];
      }
    });
  };

  selectStopsValue = (val) => {
    this.stops.value = parseInt(val);
    this.resultsEventsChannel.publish('filters.stops.change');
  };

  resetStopsValue = () => {
    this.stops.value = this.stops.options[this.stops.options.length - 1].count;
  };

  toggleAllProviders = (forced) => {
    this.providers.selectedOptions = forced || !this.allProvidersAreChecked
      ? this.providers.options.map(({name}) => name)
      : [];
    this.resultsEventsChannel.publish('filters.provider.change');
  };

  resetAllFilters = () => {
    this.resetPriceValue();
    this.resetStopsValue();
    this.toggleAllAirlines(true);
    this.toggleAllProviders(true);
    this.toggleAllTransportations(true);
    this.toggleAirlinesWithCombinations(true);
    ['obTimes', 'ibTimes'].forEach((way) =>
      ['departure', 'arrival', 'duration'].forEach((type) => this.resetFlightTimeValue(way, type)())
    );
    this.resultsEventsChannel.publish('filters.reset.change');
  };

  get allTransportationsAreChecked() {
    return this.transportTypes.options.length === this.transportTypes.selectedOptions.length;
  }

  get allAirlinesAreChecked() {
    return this.airlines.options.length === this.airlines.selectedOptions.length;
  }

  toggleAirline = (val) => {
    const selectedIndex = this.airlines.selectedOptions.indexOf(val);
    if (selectedIndex !== -1) {
      this.airlines.selectedOptions.splice(selectedIndex, 1);
    } else {
      this.airlines.selectedOptions.push(val);
    }
    this.resultsEventsChannel.publish('filters.airline.change');
  };

  selectSingleAirline = (val) => {
    this.airlines.withCombinations = false;
    this.airlines.selectedOptions = [val];
    this.resultsEventsChannel.publish('filters.airlineOnly.change');
  };

  toggleAllAirlines = (forced) => {
    this.airlines.selectedOptions = forced || !this.allAirlinesAreChecked
      ? this.airlines.options.map(({code}) => code)
      : [];
    this.resultsEventsChannel.publish('filters.airline.change');
  };

  toggleAirlinesWithCombinations = (forced) => {
    this.airlines.withCombinations = this.airlines.withCombinations === null && !forced
      ? false
      : forced || !this.airlines.withCombinations;
    this.resultsEventsChannel.publish('filters.airline.change');
  };

  get withProviders() {
    return this.providers.options.length > 1;
  }

  get allProvidersAreChecked() {
    return this.providers.options.length === this.providers.selectedOptions.length;
  }

  setSortingType = (selectedValue) => {
    this.sorting.forEach((sort) => {
      sort.selected = sort.value === selectedValue;
    });
    const eventData = {
      label: getSortEventLabel(selectedValue)
    };
    this.resultsEventsChannel.publish('sorting.change', eventData);
  };

  onPriceChange = () => {
    this.resultsEventsChannel.publish('filters.price.change');
  };

  onDepartureTimeChange = () => {
    this.resultsEventsChannel.publish('filters.departureTime.change');
  };

  onDepartureDurationChange = () => {
    this.resultsEventsChannel.publish('filters.departureDurationTime.change');
  };

  onReturnTimeChange = () => {
    this.resultsEventsChannel.publish('filters.returnTime.change');
  };

  onReturnDurationChange = () => {
    this.resultsEventsChannel.publish('filters.returnDuration.change');
  };
}

decorate(FilterStore, {
  allProvidersAreChecked: computed,
  allAirlinesAreChecked: computed,
  setupFilters: action,
  updatePriceValue: action,
  resetPriceValue: action,
  selectStopsValue: action,
  resetStopsValue: action,
  toggleAirline:action,
  selectSingleAirline: action,
  selectSingleProvider: action,
  toggleAllAirlines: action,
  toggleProvider: action,
  toggleAirlinesWithCombinations: action,
  resetAllFilters: action,
  setSortingType: action,
  allTransportationsAreChecked: computed,
  selectSingleTransportation: action,
  toggleAllTransportations: action,
  toggleTransportation:action
});

export default FilterStore;
